/**
 * Presentation statuses derived from the typed Stack lifecycle and latest
 * operation, and the plain language the UI shows them in.
 *
 * Same reason as the environment vocabulary next door: the app's My stacks
 * table and the stack-entity plugin's General card both name a stack's state,
 * and they must name it the same way. The platform's own value is what is kept
 * and compared - it is what the API returns and what a Terraform user reads -
 * and the label is only ever for display, because "DESTROYING" is precise but
 * reads as an incident.
 */
export type StackStatus =
  | 'CREATING'
  | 'READY'
  | 'UPDATING'
  | 'ACTION_RUNNING'
  | 'STOPPING'
  | 'STOPPED'
  | 'STARTING'
  | 'RESETTING'
  | 'DESTROYING'
  | 'DESTROYED'
  | 'FAILED'
  | 'CANCELLED';

export const STATUS_LABELS: Record<string, string> = {
  CREATING: 'Provisioning',
  READY: 'Ready',
  UPDATING: 'Updating',
  ACTION_RUNNING: 'Running action',
  STOPPING: 'Stopping',
  STOPPED: 'Stopped',
  STARTING: 'Starting',
  RESETTING: 'Restarting',
  DESTROYING: 'Deleting',
  DESTROYED: 'Deleted',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

/**
 * The status in plain language, falling back to the raw value so a state the
 * orchestrator adds later shows up as itself rather than as a blank.
 */
export const statusLabel = (status: string): string =>
  STATUS_LABELS[status] ?? status;

/**
 * The statuses whose stacks the day-2 update form will edit.
 *
 * This is a UI policy, not a mirror of an API rule. The orchestrator gates
 * update, destroy and the power actions on `status == "DESTROYED"` alone and
 * accepts every other value, so this list is deliberately the stricter of the
 * two - which is worth stating here, because a reader who assumes it mirrors
 * the backend will widen it expecting the API to catch the rest.
 *
 * It lives beside the vocabulary because the app wrote the same three values
 * out twice, once in the parameters field and once in that field's form-field
 * validation, and they had already begun to answer separately.
 *
 * The orchestrator's destroy-template.yaml carried the same three values as its
 * own status enum and is no longer a copy of this list: destroy has its own,
 * wider set - everything except DESTROYING and DESTROYED - because the narrow
 * one left a stopped stack with no route to deletion (#13429). It cannot import
 * from here, so it is pinned by a test in that repo instead.
 */
export const EDITABLE_STACK_STATUSES: StackStatus[] = [
  'READY',
  'UPDATING',
  'FAILED',
];

export const isEditableStackStatus = (status: string | undefined): boolean =>
  EDITABLE_STACK_STATUSES.includes(status as StackStatus);
